// src/components/PushRecipientRolesPanel.jsx
// Extracted verbatim from NotificationSettings.jsx's "Per-role notification
// routing" section -- same network calls (notification_role_settings.php),
// same NotificationRoleSettings table, same behavior. Pulled out so this
// exact panel can also render on the Role & Path Management page, without
// duplicating the fetch/save logic in two places that could drift apart.
import React, { useEffect, useState } from "react";
import { FaUserShield, FaTrash, FaPlus } from "react-icons/fa";
import { authFetch } from "../utils/api";
import { OFFICER_ROLES } from "../config/roles";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

// Suggestions only (the <input> accepts any role string, matching however
// it's actually spelled in MemberBasic.Role) -- OFFICER_ROLES is the same
// list already used elsewhere in the app to decide who's an "officer".
const KNOWN_ROLE_SUGGESTIONS = [...new Set(OFFICER_ROLES)];

export default function PushRecipientRolesPanel() {
  const [roleRows, setRoleRows] = useState([]);
  const [defaultPushRoles, setDefaultPushRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [newRoleName, setNewRoleName] = useState("");
  const [savingRole, setSavingRole] = useState("");

  const loadRoleSettings = () => {
    setLoadingRoles(true);
    authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          setRoleRows(d.rows || []);
          setDefaultPushRoles(d.default_push_roles || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  };

  useEffect(() => { loadRoleSettings(); }, []);

  const saveRoleRow = async (roleName, getsPush, getsEmail) => {
    if (!roleName.trim()) return;
    setSavingRole(roleName);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php`, {
        method: "POST",
        body: JSON.stringify({ role_name: roleName.trim(), gets_push: getsPush, gets_email: getsEmail }),
      });
      const data = await res.json();
      if (data?.success) {
        setRoleRows((prev) => {
          const others = prev.filter((r) => r.RoleName !== roleName.trim());
          return [...others, { RoleName: roleName.trim(), GetsPush: getsPush ? 1 : 0, GetsEmail: getsEmail ? 1 : 0 }]
            .sort((a, b) => a.RoleName.localeCompare(b.RoleName));
        });
        setNewRoleName("");
      }
    } catch (e) {
      // non-critical -- row just won't update visually
    } finally {
      setSavingRole("");
    }
  };

  const removeRoleRow = async (roleName) => {
    setSavingRole(roleName);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php?role_name=${encodeURIComponent(roleName)}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data?.success) {
        setRoleRows((prev) => prev.filter((r) => r.RoleName !== roleName));
      }
    } catch (e) {
      // non-critical
    } finally {
      setSavingRole("");
    }
  };

  return (
    <div className="panel" style={{ padding: "var(--sp-4)" }}>
      <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FaUserShield size={14} style={{ color: "var(--accent)" }} /> Who Gets Notified
        </h2>
      </div>
      <p className="field-hint" style={{ marginBottom: "var(--sp-4)" }}>
        Controls both birthday/event reminders and the Installation live-progress push. <b style={{ color: "var(--text-1)" }}>Push</b> = this role
        receives the admin push notification. <b style={{ color: "var(--text-1)" }}>Email</b> = a member with
        this role gets the automated birthday email on their birthday. Leave empty to use the default (Push:{" "}
        {defaultPushRoles.join(", ") || "System Admin, President"}; Email: everyone except Club Member).
      </p>

      {loadingRoles ? (
        <div className="empty-state"><span className="spinner lg" /></div>
      ) : (
        <>
          <div className="overflow-x-auto" style={{ marginBottom: "var(--sp-4)" }}>
            <table>
              <thead>
                <tr>
                  <th>Role</th>
                  <th style={{ textAlign: "center" }}>Push</th>
                  <th style={{ textAlign: "center" }}>Email</th>
                  <th style={{ textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {roleRows.length === 0 ? (
                  <tr><td colSpan={4} style={{ color: "var(--text-3)" }}>No roles configured yet -- using defaults for everyone.</td></tr>
                ) : (
                  roleRows.map((row) => (
                    <tr key={row.RoleName}>
                      <td style={{ fontWeight: 600 }}>{row.RoleName}</td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={!!Number(row.GetsPush)}
                          disabled={savingRole === row.RoleName}
                          onChange={(e) => saveRoleRow(row.RoleName, e.target.checked, !!Number(row.GetsEmail))}
                          aria-label={`${row.RoleName} gets push`}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={!!Number(row.GetsEmail)}
                          disabled={savingRole === row.RoleName}
                          onChange={(e) => saveRoleRow(row.RoleName, !!Number(row.GetsPush), e.target.checked)}
                          aria-label={`${row.RoleName} gets email`}
                        />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          onClick={() => removeRoleRow(row.RoleName)}
                          disabled={savingRole === row.RoleName}
                          className="icon-btn"
                          title="Remove (reverts to default)"
                          aria-label={`Remove ${row.RoleName} override`}
                        >
                          <FaTrash size={12} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="Role name (e.g. Secretary)"
              aria-label="Role name to add"
              list="known-roles-push-recipients"
              style={{ maxWidth: 260 }}
            />
            <datalist id="known-roles-push-recipients">
              {KNOWN_ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
            </datalist>
            <button
              onClick={() => saveRoleRow(newRoleName, true, true)}
              disabled={!newRoleName.trim() || savingRole === newRoleName.trim()}
              className="btn btn-primary btn-sm"
            >
              <FaPlus size={11} /> Add (Push + Email)
            </button>
          </div>
        </>
      )}
    </div>
  );
}
